import { DomainEntity, Id, Json } from '@homeleite/arch';
import { Status } from './status.enum';

export class MilkingListItem {
    constructor(
        public readonly id: string = Id.gen(),
        public totalMilkedAnimals = 0
    ) {}

    static fromJson(json?: any): MilkingListItem {
        return json
            ? new MilkingListItem(json.id, Number(json.totalMilkedAnimals) || 0)
            : null;
    }
}

export class MilkControlSummary implements DomainEntity {
    constructor(
        public readonly id = Id.gen(),
        public startedAt: Date = null,
        public finishedAt: Date = null,
        public status: string = Status.OPEN,
        public milkingsCount = 0,
        public milkings: MilkingListItem[] = [],
        /** APP | SRG_SCL */
        public origin: string | null = null
    ) {}

    get originLabel(): string {
        if (this.origin === 'APP') return 'App';
        if (this.origin === 'SRG_SCL') return 'SRG-SCL';
        return '—';
    }

    get originClass(): string {
        if (this.origin === 'APP') return 'ok';
        if (this.origin === 'SRG_SCL') return 'warn';
        return 'off';
    }

    get formattedStartDate(): string {
        if (!this.startedAt) return '';
        return new Date(this.startedAt).toLocaleDateString('pt-BR');
    }

    get formattedStartTime(): string {
        if (!this.startedAt) return '';
        return new Date(this.startedAt).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    get isOpenStatus(): boolean {
        return this.status === Status.OPEN;
    }

    get controlStatus(): string {
        return this.isOpenStatus ? 'ABERTO' : 'FINALIZADO';
    }

    toJson(): any {
        return Json.to.simple(this, {
            startedAt: this.startedAt?.toISOString() ?? null,
            finishedAt: this.finishedAt?.toISOString() ?? null,
            milkings: this.milkings?.map((m) => ({
                id: m.id,
                totalMilkedAnimals: m.totalMilkedAnimals
            }))
        });
    }

    /** APP | SRG_SCL — usa `origin` da API ou inferência via auditData (API antiga / listFull). */
    static originFrom(json?: any): string | null {
        const raw = String(json?.origin || '')
            .trim()
            .toUpperCase();
        if (raw === 'APP') return 'APP';
        if (raw === 'SRG_SCL' || raw === 'SRG-SCL' || raw === 'SRGSCL') return 'SRG_SCL';

        const audit = json?.auditData;
        if (!audit) return null;
        if (audit.createdByApp) return 'APP';
        if (audit.createdByIntegration) return 'SRG_SCL';
        if (audit.modifiedByIntegration) return 'SRG_SCL';
        return null;
    }

    static fromJson(json?: any): MilkControlSummary {
        if (!json) return null;
        try {
            const milkingsRaw = json.milkings;
            const milkings = Array.isArray(milkingsRaw)
                ? milkingsRaw
                      .map((m: any) =>
                          MilkingListItem.fromJson({
                              id: m?.id ?? m?._id,
                              totalMilkedAnimals:
                                  m?.totalMilkedAnimals ??
                                  (Array.isArray(m?.milkedAnimals) ? m.milkedAnimals.length : 0)
                          })
                      )
                      .filter(Boolean)
                : [];
            const startedAt = json.startedAt ? new Date(json.startedAt) : null;
            const finishedAt = json.finishedAt ? new Date(json.finishedAt) : null;
            return new MilkControlSummary(
                json.id,
                startedAt && !Number.isNaN(startedAt.getTime()) ? startedAt : null,
                finishedAt && !Number.isNaN(finishedAt.getTime()) ? finishedAt : null,
                json.status ?? Status.OPEN,
                Number(json.milkingsCount) || milkings.length || 0,
                milkings,
                MilkControlSummary.originFrom(json)
            );
        } catch {
            return null;
        }
    }

    /** Converte summary/listFull (MilkControl / DTO) em summary com origem — sem toJson pesado. */
    static fromControlLike(row: any): MilkControlSummary {
        if (!row) return null;
        try {
            const milkings = Array.isArray(row.milkings)
                ? row.milkings.map((m: any) => ({
                      id: m?.id ?? m?._id,
                      totalMilkedAnimals:
                          m?.totalMilkedAnimals ??
                          (Array.isArray(m?.milkedAnimals) ? m.milkedAnimals.length : 0)
                  }))
                : [];
            const audit =
                row.auditData?.toJson?.() ??
                (row.auditData && typeof row.auditData === 'object' ? row.auditData : null);
            return MilkControlSummary.fromJson({
                id: row.id,
                startedAt: row.startedAt,
                finishedAt: row.finishedAt,
                status: row.status,
                milkingsCount: row.milkingsCount,
                milkings,
                origin: row.origin,
                auditData: audit
            });
        } catch {
            return null;
        }
    }
}
